import {
  DISPLAY_ADS_LIBRARY_ENABLED,
  FREESTAR_ENABLED,
} from 'src/common/constants/experiments/ads';
import { selectExperiment } from 'src/common/selectors/config';
import { storeSingleton } from '../store/storeSingleton';

// Opt-in ad-targeting parity dump. Both display pipelines (Mint and @tunein/display-ads) run on
// googletag underneath — FreeStar wraps GPT — so one native `slotRequested` listener captures the
// page + slot key-values exactly as GAM serializes them into the ad request, for either arm.
// Enable with `adsTargetingDebug.enable()` in the console (or `?adsTargetingDebug`), then collect
// via `adsTargetingDebug.copy()`.

const LOG_TAG = '[ADS-TARGETING]';
const FLAG = 'adsTargetingDebug';
const OFF_VALUES = new Set(['0', 'false', 'off']);

function isFlagOn(value) {
  return value != null && !OFF_VALUES.has(String(value).trim().toLowerCase());
}

function isEnabled() {
  try {
    const query = new URLSearchParams(window.location.search).get(FLAG);
    // A bare `?adsTargetingDebug` reads as '' — treat presence as on unless explicitly off.
    return query != null ? isFlagOn(query || '1') : isFlagOn(window.localStorage?.getItem(FLAG));
  } catch {
    return false;
  }
}

function readTargeting(source) {
  const targeting = {};

  // Sorted so page/slot targeting diffs cleanly between the Mint and display-ads arms,
  // regardless of the (unspecified) key order each pipeline happens to populate GPT in.
  for (const key of [...source.getTargetingKeys()].sort()) {
    targeting[key] = source.getTargeting(key);
  }

  return targeting;
}

export function initAdsTargetingDebug() {
  const dump = (window.__ADS_TARGETING_DUMP__ ??= []);

  // Console-facing switch so enabling never depends on remembering the exact flag value. Assigned
  // unconditionally: `typeof adsTargetingDebug` is also the "is my bundle fresh?" check.
  window.adsTargetingDebug = {
    enabled: isEnabled(),
    enable() {
      window.localStorage.setItem(FLAG, '1');
      window.location.reload();
    },
    disable() {
      window.localStorage.removeItem(FLAG);
      window.location.reload();
    },
    copy() {
      return JSON.stringify(dump, null, 2);
    },
  };

  if (!isEnabled()) {
    return;
  }

  console.info(LOG_TAG, 'enabled — waiting for googletag to load');

  const googletag = (window.googletag ??= { cmd: [] });

  (googletag.cmd ??= []).push(() => {
    window.googletag.pubads().addEventListener('slotRequested', (event) => {
      try {
        const state = storeSingleton.state;
        const entry = {
          t: new Date().toISOString(),
          arm: selectExperiment(state, DISPLAY_ADS_LIBRARY_ENABLED, false) ? 'display-ads' : 'mint',
          freestar: Boolean(selectExperiment(state, FREESTAR_ENABLED, false)),
          elementId: event.slot.getSlotElementId(),
          adUnitPath: event.slot.getAdUnitPath(),
          page: readTargeting(window.googletag.pubads()),
          slot: readTargeting(event.slot),
        };

        dump.push(entry);
        // One JSON object per line so the dump greps and diffs cleanly.
        console.info(LOG_TAG, JSON.stringify(entry));
      } catch (error) {
        console.warn(LOG_TAG, 'failed to capture slot request', error);
      }
    });

    console.info(
      LOG_TAG,
      'capturing googletag slot requests; collect with copy(adsTargetingDebug.copy())',
    );
  });
}
